import React from "react";
import PropTypes from "prop-types";
import { FileSpreadsheet } from "lucide-react";

import Card from "../../../../../../../components/Card";
import { UIButton } from "../../../../../../../components/ui";
import { clx } from "../../../../../../../utils/Helpers";

const openDocument = (url) => {
  if (!url) return;
  window.open(url, "_blank", "noopener,noreferrer");
};

const DocumentIcon = ({ tone }) => (
  <span
    className={clx(
      "review-document-changes__file-icon",
      `review-document-changes__file-icon--${tone}`,
    )}
    aria-hidden="true"
  >
    <FileSpreadsheet size={15} strokeWidth={1.5} />
  </span>
);

DocumentIcon.propTypes = {
  tone: PropTypes.oneOf(["current", "new"]).isRequired,
};

const ReviewDocumentChanges = ({ title, fields, cl }) => {
  return (
    <Card
      title={cl.documentChanges ?? title}
      classNames={{
        root: "review-document-changes",
        body: "review-document-changes__body",
      }}
    >
      <div className="review-document-changes__table">
        <div className="review-document-changes__head review-document-changes__cell--type">
          {cl.documentType}
        </div>
        <div className="review-document-changes__head">
          {cl.currentDocument}
        </div>
        <div className="review-document-changes__head review-document-changes__head--new">
          {cl.newSubmission}
        </div>

        {fields.map((field) => {
          const currentUrl = field.oldFileUrl || field.fileUrl;
          const newUrl = field.newFileUrl;
          const currentName = field.oldValue || field.display;
          const newName = field.newValue;
          const hasNewSubmission = field.changed && Boolean(newUrl);

          return (
            <React.Fragment key={field.label}>
              <div className="review-document-changes__type">{field.label}</div>
              <div
                className={clx(
                  "review-document-changes__doc-cell",
                  field.changed && "review-document-changes__doc-cell--changed",
                )}
              >
                <DocumentIcon tone="current" />
                <div className="review-document-changes__doc-meta">
                  <div className="review-document-changes__current-name">
                    {currentName}
                  </div>
                  {currentUrl ? (
                    <button
                      type="button"
                      className={clx(
                        "review-document-changes__text-action review-document-changes__text-action--primary",
                        { "view-old-docs": field.changed },
                      )}
                      onClick={() => openDocument(currentUrl)}
                    >
                      {field.changed ? cl.viewOldDoc : cl.viewCurrent}
                    </button>
                  ) : null}
                </div>
              </div>
              <div
                className={clx(
                  "review-document-changes__doc-cell",
                  "review-document-changes__doc-cell--new",
                  !hasNewSubmission &&
                    "review-document-changes__doc-cell--empty",
                )}
              >
                {hasNewSubmission ? (
                  <>
                    <DocumentIcon tone="new" />
                    <div className="review-document-changes__doc-meta">
                      <div className="review-document-changes__new-name">
                        {newName}
                      </div>
                      <div className="review-document-changes__new-actions">
                        <UIButton
                          type="button"
                          variant="success"
                          size="small"
                          onClick={() => openDocument(newUrl)}
                          className="review-document-changes__preview-button"
                        >
                          {cl.previewNew}
                        </UIButton>
                        <button
                          type="button"
                          className="review-document-changes__text-action review-document-changes__text-action--success"
                          onClick={() => openDocument(newUrl)}
                        >
                          {cl.download}
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <span className="review-document-changes__empty-text">
                    {cl.noUpdateSubmittedForDocument}
                  </span>
                )}
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </Card>
  );
};

ReviewDocumentChanges.propTypes = {
  title: PropTypes.string.isRequired,
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      changed: PropTypes.bool.isRequired,
      display: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      oldValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      newValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      fileUrl: PropTypes.string,
      oldFileUrl: PropTypes.string,
      newFileUrl: PropTypes.string,
    }),
  ).isRequired,
  cl: PropTypes.object.isRequired,
};

export default ReviewDocumentChanges;
